import * as React from "react";

import { Alert, Snackbar, Stack, Typography } from "@mui/material";

import { colors } from "@styles/constants";
import { Document } from "../../../types/Document";
import { CompanyType, prettyTitle, readableRelationship } from "../../../types/NotifierFormData";
import { formatDate } from "@utils/Functions";
import { Header } from "../../../components/atoms/Header";
import { HR } from "../../../components/atoms/HR";
import { Link } from "../../../components/atoms/Link";
import {
  FieldRow,
  FieldsTable,
  FieldsTableHeader,
} from "../../../components/molecules/FieldsTable";
import { InfoBoxTrigger } from "../../../components/molecules/InfoBoxTrigger";
import { UpdateFormTemplateProc } from "@templates/FormTemplate";
import { getExpressData, getExpressSectionsData, setExpressSubmitted } from "../express-data";
import { AIGAccount } from "./AIGAccount";
import { BankAccount } from "./BankAccount";
import { CreditCardsAccount } from "./CreditCardAccount";
import { DefaultAccount } from "./DefaultAccount";
import { EnergyAccount } from "./EnergyAccount";
import { HastingsDirectAccount } from "./HastingsDirectAccount";
import { InsuranceAccount } from "./InsuranceAccount";
import { InvestmentsAccount } from "./InvestmentsAccount";
import { MobileAccount } from "./MobileAccount";
import { MortgageAccount } from "./MortgageAccount";
import { PensionAccount } from "./PensionAccount";
import { SocialMediaOrEmailAccount } from "./SocialMediaOrEmailAccount";
import { WaterAccount } from "./WaterAccount";
import { submit } from "@api/formApi";

type ServiceProviderDetails = {
  companyName: string;
  deathCertificateNeeded?: boolean;
  serviceProviderType: CompanyType;
  idDocumentNeeded?: boolean;
  customForm?: string;
};

export type SubmitProps = {
  readonly serviceProvider: ServiceProviderDetails;
  readonly onSectionClick: (section: string) => void;
  readonly updateTemplate: UpdateFormTemplateProc;
  uploadedFileInfo: (id: string) => Promise<Document>;
  next: () => void;
};

export const Submit: React.FunctionComponent<SubmitProps> = ({
  serviceProvider,
  onSectionClick,
  updateTemplate,
  uploadedFileInfo,
  next,
}) => {
  const [{ account, notifier }, setLocalState] = React.useState({
    account: undefined,
    notifier: undefined,
  });

  React.useEffect(() => {
    const data = getExpressSectionsData();
    if (!data) {
      return;
    }
    const account = data.account?.target;
    const notifier = data.notifier?.target;
    setLocalState({ account, notifier });
  }, []);

  const [{ busy, error }, setState] = React.useState({
    busy: false,
    error: undefined,
  } as { busy: boolean; error?: string });

  const onSubmit = React.useCallback(() => {
    const slug = getExpressData()?.slug;
    const data = getExpressSectionsData();

    const account = data?.account?.target;
    const documents = data?.documents?.target;
    const notifier = data?.notifier?.target;
    const kyc = data?.kyc?.target;

    if (
      !slug ||
      !account ||
      !notifier ||
      (serviceProvider.deathCertificateNeeded && !documents) ||
      (serviceProvider.idDocumentNeeded && !kyc)
    ) {
      setState({
        busy: false,
        error:
          "Please verify that all steps are valid (have a check on the menu) before submitting.",
      });
      return;
    }

    setState({ busy: true });

    if (account?.property?.occupier?.id === "notifier") {
      account.property.occupier.notifier = true;
      delete account.property.occupier.id;
    }

    if (account?.responsible?.id === "notifier") {
      account.responsible.notifier = true;
      delete account.responsible.id;
    }

    submit({ data: { slug, account, documents: documents || {}, notifier, kyc: kyc || {} } }).then(
      ({ formUrl }) => {
        setState({ busy: false });
        setExpressSubmitted(formUrl);
        next();
      },
      (error) => {
        if (error.message === "email_taken") {
          setState({
            busy: false,
            error:
              "Your email address has already been used. Please contact customer support for help.",
          });
          return;
        }

        setState({
          busy: false,
          error: "Unexpected failure. Please try again or contact customer support.",
        });
      }
    );
  }, [next]);

  const onEditNotifier = React.useCallback(() => {
    onSectionClick("notifier");
  }, [onSectionClick]);

  const onEditAccount = React.useCallback(() => {
    onSectionClick("account");
  }, [onSectionClick]);

  return (
    <SubmitView
      serviceProvider={serviceProvider}
      account={account}
      notifier={notifier}
      error={error}
      busy={busy}
      onSubmit={onSubmit}
      onSectionClick={onSectionClick}
      updateTemplate={updateTemplate}
      onEditNotifier={onEditNotifier}
      onEditAccount={onEditAccount}
      uploadedFileInfo={uploadedFileInfo}
      removeRemoteError={() => {}}
    />
  );
};

export type SubmitViewProps = {
  readonly serviceProvider: ServiceProviderDetails;
  readonly account: any;
  readonly notifier: any;
  readonly error?: string;
  readonly remoteError?: string;
  readonly busy: boolean;

  readonly updateTemplate: UpdateFormTemplateProc;
  readonly removeRemoteError: () => void;
  readonly onSubmit: () => void;
  readonly onSectionClick: (section: string) => void;
  readonly onEditNotifier: () => void;
  readonly onEditAccount: () => void;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
};

export const SubmitView: React.FC<SubmitViewProps> = ({
  serviceProvider,
  account,
  notifier,

  error,
  remoteError,
  busy,
  onSubmit,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  onEditNotifier,
  onEditAccount,
  uploadedFileInfo,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection: "submit",
      nextLabel: "Submit",
      saveLabel: null,
      onNext: onSubmit,
      onSectionClick,
    });
  }, [busy, onSubmit, onSectionClick, updateTemplate]);

  const accountSectionTitle =
    serviceProvider.serviceProviderType === CompanyType.Insurance
      ? "Policy details for the person who died"
      : "Account details for the person who died";

  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Header level={1} color={colors.accentTeal}>
          Review and Submit
        </Header>

        <Typography variant="body1">
          The information you have provided will be used to notify {serviceProvider.companyName}.
          Please review the information below before pressing ‘submit’.{" "}
          <InfoBoxTrigger
            white
            width="448px"
            content={
              <div>
                <p>
                  Service Providers need to see that you have given Settld your permission to
                  contact them. This permission is called a ‘grant of authority’. When you hit
                  submit on the form, you are providing Settld with a grant of authority for up to
                  12 months.
                </p>

                <p>
                  <b>What am I giving Settld permission to do?</b>
                </p>
                <ul>
                  <li>
                    Notify Service Providers of the death, using the information in the form.{" "}
                  </li>
                  <li>Communicate and share information with Service Providers on your behalf.</li>
                  <li>
                    Request Service Providers to close, transfer, amend or gain information about
                    the accounts of the Deceased for you.
                  </li>
                  <li>Receive date of death balances for the accounts of the Deceased.</li>
                </ul>

                <p>
                  <b>Please note, Settld is not permitted to:</b>
                </p>
                <ul>
                  <li>Sign account closure forms on your behalf.</li>
                  <li>
                    Collect money from Service Providers, in respect of the Deceased accounts.
                  </li>
                </ul>

                <p>
                  <b>Is there anything else I should be aware of?</b>
                </p>
                <p>
                  By submitting the form, you are confirming that you are at least 18 years of age
                  and are authorised to act as the Executor or personal representative of the person
                  who died. By agreeing to proceed, you also acknowledge that you have read and
                  understood the Settld{" "}
                  <Link
                    inline
                    target="_blank"
                    to="https://www.settld.care/legal/terms-and-conditions.html"
                  >
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    inline
                    target="_blank"
                    to="https://www.settld.care/legal/privacy-policy.html"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            }
          />
        </Typography>
      </Stack>

      <HR />

      {!account ? (
        <Stack rowGap={2}>
          <FieldsTableHeader title={accountSectionTitle} onEdit={onEditAccount} />
          <Typography variant="body1" color={colors.fadedText}>
            Not completed yet
          </Typography>
        </Stack>
      ) : (
        <FieldsTable
          header={<FieldsTableHeader title={accountSectionTitle} onEdit={onEditAccount} />}
        >
          {serviceProvider.customForm === "aig" ? (
            <AIGAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.customForm === "hastings-direct" ? (
            <HastingsDirectAccount account={account} />
          ) : serviceProvider.serviceProviderType === CompanyType.Banking ||
            serviceProvider.serviceProviderType === CompanyType.BuildingSociety ? (
            <BankAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.CreditCards ? (
            <CreditCardsAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Energy ? (
            <EnergyAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Insurance ? (
            <InsuranceAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Investments ? (
            <InvestmentsAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Mobile ? (
            <MobileAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Mortgage ? (
            <MortgageAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Pension ? (
            <PensionAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.SocialMedia ||
            serviceProvider.serviceProviderType === CompanyType.Email ? (
            <SocialMediaOrEmailAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : serviceProvider.serviceProviderType === CompanyType.Water ? (
            <WaterAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          ) : (
            <DefaultAccount
              account={account}
              serviceProviderType={serviceProvider.serviceProviderType}
            />
          )}
        </FieldsTable>
      )}

      <HR />

      {!notifier ? (
        <Stack rowGap={2}>
          <FieldsTableHeader title="Your details" onEdit={onEditNotifier} />
          <Typography variant="body1" color={colors.fadedText}>
            Not completed yet
          </Typography>
        </Stack>
      ) : (
        <FieldsTable header={<FieldsTableHeader title="Your details" onEdit={onEditNotifier} />}>
          <FieldRow
            label="Name"
            value={`${prettyTitle(notifier.title)} ${notifier.firstName} ${notifier.lastName} `}
          />
          <FieldRow
            label="Date of birth"
            value={notifier.dateOfBirth ? formatDate(notifier.dateOfBirth) : ""}
          />
          <FieldRow
            label="Address"
            value={
              <>
                {notifier.address}
                <br />
                {notifier.city}
                <br />
                {notifier.postcode}
              </>
            }
          />
          <FieldRow label="Phone" value={notifier.phoneNumber || ""} />
          <FieldRow label="Email" value={notifier.emailAddress || ""} />
          <FieldRow label="Vulnerable?" value={notifier.isVulnerable ? "Yes" : "No"} />
          <FieldRow
            label="The person who died was"
            value={readableRelationship(notifier.relationshipToDeceased) || ""}
          />
          <FieldRow label="Role" value={readableRole(notifier.role) || ""} />
        </FieldsTable>
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteError || !!error}
        autoHideDuration={6000}
        onClose={removeRemoteError}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={removeRemoteError}>
          {remoteError || error}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

const readableRole = (role: string) => {
  switch (role) {
    case "sole_executor":
      return "Sole Executor";
    case "executor":
      return "Executor, alongside others";
    case "administrator":
      return "Administrator";
    case "delegated_notifier":
      return "Notifying on behalf of someone else";
  }
};
